import { logger } from '../utils/logger';



// --- API-запросы ---

const API_BASE_URL = '/api';
let systemLinksRequest = null;

// Функция для логирования только в режиме разработки
const devLog = (...args) => {
  logger.dev(...args);
};

// Получить системные ссылки (бота и поддержки)
export const fetchSystemLinks = async () => {
  const cacheKey = 'systemLinks';
  const cached = cache.get(cacheKey);

  if (isCacheValid(cached)) {
    devLog('🔗 fetchSystemLinks: возвращаю данные из кэша', cached.data);
    return cached.data;
  }

  if (systemLinksRequest) {
    devLog('🔗 fetchSystemLinks: использую активный запрос');
    return systemLinksRequest;
  }

  devLog('🔗 fetchSystemLinks: начало выполнения');
  systemLinksRequest = (async () => {
    try {
      devLog('🔗 fetchSystemLinks: отправляю запрос на', `${API_BASE_URL}/system/links`);
      const response = await fetch(`${API_BASE_URL}/system/links`);
      devLog('🔗 fetchSystemLinks: получен ответ', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`Ошибка при получении системных ссылок: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      devLog('🔗 fetchSystemLinks: получены данные', data);

      const result = {
        bot: data.botLink || '',
        support: data.supportBotLink || '',
        visitorCountryCode: data.visitorCountryCode || '',
        isRestrictedRegion: data.isRestrictedRegion === true,
      };

      cache.set(cacheKey, {
        data: result,
        timestamp: Date.now()
      });
      cleanCache();

      devLog('🔗 fetchSystemLinks: возвращаю результат', result);
      return result;
    } catch (error) {
      logger.error('❌ fetchSystemLinks: ошибка', error);
      if (cached?.data) {
        devLog('⚠️ fetchSystemLinks: возвращаю устаревшие данные из кэша');
        return cached.data;
      }

      const fallback = {
        bot: '',
        support: '',
        visitorCountryCode: '',
        isRestrictedRegion: false,
      };
      devLog('🔗 fetchSystemLinks: возвращаю fallback', fallback);
      return fallback;
    } finally {
      systemLinksRequest = null;
    }
  })();

  return systemLinksRequest;
};

// Константы для кэширования (оптимизировано для слабого сервера)
const CACHE_TTL = 10 * 60 * 1000; // 10 минут (увеличили интервал)
const MAX_CACHE_SIZE = 50; // Максимальное количество записей в кэше
const cache = new Map();

// Функция для проверки актуальности кэша
const isCacheValid = (cachedData) => {
  if (!cachedData || !cachedData.timestamp) return false;
  return (Date.now() - cachedData.timestamp) < CACHE_TTL;
};

// Функция для очистки старых записей кэша
const cleanCache = () => {
  if (cache.size <= MAX_CACHE_SIZE) return;
  
  const entries = Array.from(cache.entries());
  
  // Сначала удаляем устаревшие записи (TTL истек)
  const now = Date.now();
  const expiredKeys = [];
  for (const [key, value] of entries) {
    if (value && value.timestamp && (now - value.timestamp) >= CACHE_TTL) {
      expiredKeys.push(key);
    }
  }
  
  // Удаляем устаревшие записи
  expiredKeys.forEach(key => cache.delete(key));
  devLog(`🗑️ Удалено ${expiredKeys.length} устаревших записей кэша`);
  
  // Если после удаления устаревших всё ещё много записей, удаляем самые старые
  if (cache.size > MAX_CACHE_SIZE) {
    const remainingEntries = Array.from(cache.entries());
    remainingEntries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    
    const toDelete = remainingEntries.slice(0, cache.size - MAX_CACHE_SIZE);
    toDelete.forEach(([key]) => cache.delete(key));
    
    devLog(`🗑️ Дополнительно удалено ${toDelete.length} старых записей кэша`);
  }
  
  devLog(`📊 Текущий размер кэша: ${cache.size}/${MAX_CACHE_SIZE} записей`);
};

export const fetchLocations = async (forceRefresh = false) => {
  const cacheKey = 'locations';
  const cached = cache.get(cacheKey);
  
  if (!forceRefresh && isCacheValid(cached)) {
    devLog(`🌍 Данные локаций из кэша:`, cached.data);
    return Promise.resolve(cached.data);
  }

  try {
    devLog('🌍 Выполняется запрос локаций');
    const response = await fetch(`${API_BASE_URL}/servers/locations`);
    if (!response.ok) {
      throw new Error('Ошибка при получении локаций');
    }
    const data = await response.json();
    
    // Сохраняем в кэш с временной меткой
    cache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
    
    // Очищаем старые записи если нужно
    cleanCache();
    
    devLog(`🌍 Получены данные локаций:`, data);
    return data;
  } catch (error) {
    logger.error('❌ Ошибка при получении локаций:', error);
    // Если есть устаревшие данные в кэше, возвращаем их
    if (cached && cached.data) {
      devLog('⚠️ Возвращаем устаревшие данные из кэша из-за ошибки API');
      return cached.data;
    }
    throw error;
  }
};

export const fetchPricing = async (forceRefresh = false) => {
  const cacheKey = 'pricing';
  const cached = cache.get(cacheKey);
  
  if (!forceRefresh && isCacheValid(cached)) {
    devLog(`💰 Данные цен из кэша:`, cached.data);
    return Promise.resolve(cached.data);
  }

  try {
    devLog('💰 Выполняется запрос цен');
    const response = await fetch(`${API_BASE_URL}/tariffs/featured`);
    if (!response.ok) {
      throw new Error('Ошибка при получении цен');
    }
    const data = await response.json();
    
    cache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
    
    cleanCache();
    
    devLog(`💰 Получены данные цен:`, data);
    return data;
  } catch (error) {
    logger.error('❌ Ошибка при получении цен:', error);
    // Если есть устаревшие данные в кэше, возвращаем их
    if (cached && cached.data) {
      devLog('⚠️ Возвращаем устаревшие данные из кэша из-за ошибки API');
      return cached.data;
    }
    throw error;
  }
};

// Функция для принудительного очищения кэша
export const clearCache = () => {
  cache.clear();
  devLog('🧹 Кэш полностью очищен');
};

// Функция для экстренной очистки кэша при нехватке памяти
export const emergencyCacheCleanup = () => {
  const entries = Array.from(cache.entries());
  
  if (entries.length === 0) return;
  
  // Удаляем 70% самых старых записей
  const toDelete = Math.floor(entries.length * 0.7);
  entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
  
  for (let i = 0; i < toDelete; i++) {
    cache.delete(entries[i][0]);
  }
  
  devLog(`🚨 Экстренная очистка: удалено ${toDelete} записей кэша`);
  return toDelete;
};

// Функция для получения информации о кэше
export const getCacheInfo = () => {
  const info = {};
  let totalSize = 0;
  let totalEntries = 0;
  
  for (const [key, value] of cache.entries()) {
    if (value && value.timestamp) {
      const age = Date.now() - value.timestamp;
      const isValid = age < CACHE_TTL;
      const dataSize = JSON.stringify(value.data).length;
      
      info[key] = {
        age: Math.round(age / 1000), // в секундах
        isValid,
        dataSize,
        sizeKB: Math.round(dataSize / 1024 * 100) / 100 // в КБ
      };
      
      totalSize += dataSize;
      totalEntries++;
    }
  }
  
  // Добавляем общую статистику
  info._summary = {
    totalEntries,
    totalSizeKB: Math.round(totalSize / 1024 * 100) / 100,
    cacheLimit: MAX_CACHE_SIZE,
    ttlMinutes: Math.round(CACHE_TTL / 60000)
  };
  
  return info;
};

// Автообновление кэша (оптимизировано для слабого сервера)
let autoRefreshInterval = null;
let cleanupInterval = null;

export const startAutoRefresh = () => {
  if (autoRefreshInterval) return; // Уже запущен
  
  devLog('🔄 Запуск автообновления кэша каждые 10 минут (оптимизировано для слабого сервера)');
  autoRefreshInterval = setInterval(async () => {
    try {
      devLog('🔄 Автообновление кэша...');
      // Обновляем по очереди, а не параллельно, чтобы не перегружать сервер
      await fetchLocations(true).catch(err => devLog('❌ Ошибка автообновления локаций:', err.message));
      // Небольшая пауза между запросами
      await new Promise(resolve => setTimeout(resolve, 1000));
      await fetchPricing(true).catch(err => devLog('❌ Ошибка автообновления цен:', err.message));
      devLog('✅ Кэш автообновлен');
    } catch (error) {
      devLog('❌ Ошибка при автообновлении кэша:', error.message);
    }
  }, CACHE_TTL);
  
  // Запускаем периодическую очистку кэша каждые 5 минут
  devLog('🧹 Запуск периодической очистки кэша каждые 5 минут');
  cleanupInterval = setInterval(() => {
    devLog('🧹 Периодическая очистка кэша...');
    cleanCache();
  }, 5 * 60 * 1000); // 5 минут
};

export const stopAutoRefresh = () => {
  if (autoRefreshInterval) {
    clearInterval(autoRefreshInterval);
    autoRefreshInterval = null;
    devLog('⏹️ Автообновление кэша остановлено');
  }
  
  if (cleanupInterval) {
    clearInterval(cleanupInterval);
    cleanupInterval = null;
    devLog('⏹️ Периодическая очистка кэша остановлена');
  }
};
